import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import * as projectService from "../services/projectService";
import type { Project } from "../types/api";
import { Skeleton } from "../components/ui/skeleton";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useToast } from "../components/ui/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { resolveAssetUrl } from "../utils/url";
import { useTranslation } from "react-i18next";
import {
  pickProjectDescription,
  pickProjectName,
} from "../utils/localeDisplay";

const ProjectsPage = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | Project["status"]>(
    "all"
  );
  const { toast } = useToast();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const data = await projectService.listProjects();
        setProjects(data);
      } catch (err) {
        toast({
          title: "Erreur",
          description: (err as string) ?? "Impossible de charger les projets",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    void fetchProjects();
  }, [toast]);

  const filteredProjects = useMemo(() => {
    const lang = i18n.language;
    return projects.filter((project) => {
      const displayName = pickProjectName(project, lang);
      const matchesSearch =
        displayName.toLowerCase().includes(search.toLowerCase()) ||
        project.symbol.toLowerCase().includes(search.toLowerCase());
      const matchesStatus =
        statusFilter === "all" || project.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [projects, search, statusFilter, i18n.language]);

  const statusStats = useMemo(() => {
    return {
      open: projects.filter((p) => p.status === "open").length,
      upcoming: projects.filter((p) => p.status === "upcoming").length,
      closed: projects.filter((p) => p.status === "closed").length,
    };
  }, [projects]);

  const getStatusBadge = (status: Project["status"]) => {
    const label =
      status === "open"
        ? t("projects.filterOpen")
        : status === "closed"
        ? t("projects.filterClosed")
        : t("projects.filterUpcoming");
    const className =
      status === "open"
        ? "border-emerald-400/40 bg-emerald-500/15 text-emerald-900 dark:text-emerald-200"
        : status === "closed"
        ? "border-rose-400/40 bg-rose-500/15 text-rose-900 dark:text-rose-200"
        : "border-amber-400/40 bg-amber-500/15 text-amber-900 dark:text-amber-200";
    return { label, className };
  };

  return (
    <section className="space-y-6">
      <div>
        <h2 className="text-3xl font-semibold text-foreground">
          {t("projects.title")}
        </h2>
        <p className="text-sm text-muted-foreground dark:text-white/70">{t("projects.subtitle")}</p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <article className="rounded-3xl border border-border bg-muted/50 p-4 dark:border-white/10 dark:bg-black/30">
          <p className="text-xs uppercase text-muted-foreground dark:text-white/60">
            {t("projects.statusOpen")}
          </p>
          <p className="text-2xl font-semibold text-emerald-700 dark:text-emerald-300">
            {statusStats.open}
          </p>
        </article>
        <article className="rounded-3xl border border-border bg-muted/50 p-4 dark:border-white/10 dark:bg-black/30">
          <p className="text-xs uppercase text-muted-foreground dark:text-white/60">
            {t("projects.statusUpcoming")}
          </p>
          <p className="text-2xl font-semibold text-cyan-700 dark:text-cyan-300">
            {statusStats.upcoming}
          </p>
        </article>
        <article className="rounded-3xl border border-border bg-muted/50 p-4 dark:border-white/10 dark:bg-black/30">
          <p className="text-xs uppercase text-muted-foreground dark:text-white/60">
            {t("projects.statusClosed")}
          </p>
          <p className="text-2xl font-semibold text-foreground dark:text-white">
            {statusStats.closed}
          </p>
        </article>
      </div>

      <div className="grid gap-4 md:grid-cols-[2fr,1fr]">
        <Input
          placeholder={t("projects.searchPlaceholder")}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="bg-background text-foreground border-border dark:bg-black/30 dark:text-white"
        />
        <Select
          value={statusFilter}
          onValueChange={(value) =>
            setStatusFilter(value as "all" | Project["status"])
          }>
          <SelectTrigger>
            <SelectValue placeholder={t("projects.filterAll")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("projects.filterAll")}</SelectItem>
            <SelectItem value="open">{t("projects.filterOpen")}</SelectItem>
            <SelectItem value="upcoming">
              {t("projects.filterUpcoming")}
            </SelectItem>
            <SelectItem value="closed">{t("projects.filterClosed")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-48 rounded-3xl" />
          ))}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredProjects.map((project) => {
            const displayName = pickProjectName(project, i18n.language);
            const displayDescription = pickProjectDescription(
              project,
              i18n.language
            );
            const imageSrc = resolveAssetUrl(project.imageUrl);
            const { label: statusLabel, className: statusClass } =
              getStatusBadge(project.status);
            return (
              <article
                key={project.id}
                className="group flex h-full flex-col rounded-3xl border border-border bg-card p-5 text-foreground shadow-sm transition-shadow hover:shadow-md dark:border-white/10 dark:bg-black/30 dark:text-white">
                {imageSrc && (
                  <div className="mb-4 overflow-hidden rounded-2xl border border-border bg-muted/40 dark:border-white/10">
                    <img
                      src={imageSrc}
                      alt={`Illustration du projet ${project.name}`}
                      className="h-40 w-full object-cover"
                      loading="lazy"
                      crossOrigin="anonymous"
                      onError={(e) => {
                        console.error(
                          "Erreur de chargement de l'image:",
                          imageSrc,
                          e
                        );
                      }}
                    />
                  </div>
                )}
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-lg font-semibold text-foreground dark:text-white">
                    {displayName}
                  </h3>
                  <span
                    className={`shrink-0 rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-widest ${statusClass}`}>
                    {statusLabel}
                  </span>
                </div>
                <p className="mt-3 line-clamp-3 text-sm text-muted-foreground dark:text-white/70">
                  {displayDescription}
                </p>
                <ul className="mt-4 space-y-1 text-sm text-muted-foreground dark:text-white/60">
                  <li>
                    {t("projects.goal")}: {project.goal.toLocaleString()} $
                  </li>
                  <li>
                    {t("projects.raised")}:{" "}
                    {project.totalRaised.toLocaleString()} $
                  </li>
                  <li>
                    {t("projects.activeContributors")}:{" "}
                    {project.activeContributors}
                  </li>
                </ul>
                <Button asChild variant="default" className="mt-5 w-full">
                  <Link to={`/projects/${project.id}`}>
                    {t("projects.viewDetails")}
                  </Link>
                </Button>
              </article>
            );
          })}
          {filteredProjects.length === 0 && (
            <p className="text-sm text-muted-foreground dark:text-white/60">
              {t("projects.empty")}
            </p>
          )}
        </div>
      )}
    </section>
  );
};

export default ProjectsPage;
